import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE, PROFILE_IMG_BASE } from "@config/env";
import { Modal, ModalFooter } from "@common/Modal";

const STATUS_STYLE = {
  "1": { bg:"var(--fc-primary-light)", color:"#2563EB", label:"Shortlisted" },
  "2": { bg:"var(--fc-amber-light)",   color:"#D97706", label:"Interview"   },
  "3": { bg:"var(--fc-green-light)",   color:"#16A34A", label:"Presented"   },
  "4": { bg:"var(--fc-red-light)",     color:"#DC2626", label:"Rejected"    },
};

const post = (ep, fields = {}) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([k,v]) => { if (v != null) fd.append(k, v); });
  return axios.post(`${API_BASE}${ep}`, fd).then(r => r.data).catch(() => null);
};


export default function JobCandidatesTab({ jobSlug, userSlug, firmId, hasUpdate, onUpdateDone }) {
  const [candidates, setCandidates] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [statusModal, setStatusModal] = useState(null); // { item }
  const [statusList,  setStatusList]  = useState([]);
  const [selStatus,   setSelStatus]   = useState("");
  const [note,        setNote]        = useState("");
  const [updating,    setUpdating]    = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    post("get-preffered-candidate", { j_slug: jobSlug, u_slug: userSlug }).then(data => {
      if (data?.status === 1) { setCandidates(data.candidate_list || []); }
      else { setError(data?.message || "No candidates found"); setCandidates([]); }
      setLoading(false);
    });
  }, [jobSlug, userSlug]);

  useEffect(() => { if (jobSlug) load(); }, [load]);
  useEffect(() => { if (hasUpdate) { load(); onUpdateDone(); } }, [hasUpdate]);

  const openStatus = async (item) => {
    setStatusModal(item); setSelStatus(item.candidate_status||""); setNote("");
    if (statusList.length === 0) {
      const data = await post("get-candidate-status-for-job", {});
      if (data?.status === 1) setStatusList(data.status_list || []);
    }
  };

  const updateStatus = async () => {
    if (!selStatus) return;
    setUpdating(true);
    const data = await post("update-candidate-status-for-job", {
      user_slug: userSlug, id: statusModal.auto_id, status: selStatus, note
    });
    setUpdating(false);
    if (data) {
      if (selStatus === "4") {
        setCandidates(c => c.filter(x => x.auto_id !== statusModal.auto_id));
      } else {
        setCandidates(c => c.map(x => x.auto_id === statusModal.auto_id
          ? { ...x, candidate_status: data.candidate_status, candidate_propose_status: data.status_title }
          : x));
      }
      setStatusModal(null);
    }
  };

  if (loading) return (
    <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:14, padding:"40px 24px", textAlign:"center" }}>
      <i className="fa-solid fa-spinner fa-spin" style={{ fontSize:20, color:"var(--fc-text-hint)", display:"block", marginBottom:10 }} />
      <div style={{ fontSize:13, color:"var(--fc-text-subtle)", fontWeight:500 }}>Loading candidates...</div>
    </div>
  );
  if (error && candidates.length===0) return <div style={{ padding:"24px 16px", background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:14, textAlign:"center", fontSize:13, color:"var(--fc-text-muted)" }}>{error}</div>;

  return (
    <>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:"var(--fc-text-primary)" }}>Proposed Candidates</div>
          <div style={{ fontSize:11, color:"var(--fc-text-subtle)", marginTop:2 }}>{candidates.length} candidate{candidates.length !== 1 ? "s" : ""} proposed for this job</div>
        </div>
        <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:99, background:"var(--fc-primary-light)", color:"#2563EB", border:"1px solid var(--fc-primary-border)" }}>
          {candidates.length} total
        </span>
      </div>

      {/* List */}
      <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:14, overflow:"hidden" }}>
        {candidates.length === 0 ? (
          <div style={{ padding:"36px 24px", textAlign:"center" }}>
            <i className="fa-solid fa-users" style={{ fontSize:24, color:"var(--fc-text-hint)", display:"block", marginBottom:10 }} />
            <div style={{ fontSize:13, color:"var(--fc-text-subtle)" }}>No candidates proposed yet</div>
          </div>
        ) : null}

        {candidates.map((item, i) => {
          const ss        = STATUS_STYLE[item.candidate_status] || { bg:"var(--fc-border-light)", color:"var(--fc-text-muted)", label:item.candidate_propose_status||"Pending" };
          const isOwnFirm = String(item.resource_firm) === String(firmId);
          const color     = ["#2563EB","#7C3AED","#16A34A","#D97706","#EF4444","#06B6D4"][i % 6];
          const imgSrc    = item.profile_image_path ? PROFILE_IMG_BASE + item.profile_image_path : null;

          return (
            <div key={item.auto_id || i}
              style={{ padding:"10px 16px", display:"flex", alignItems:"center", gap:12, borderBottom: i < candidates.length-1 ? "1px solid var(--fc-border-light)" : "none", transition:"background 0.1s" }}
              onMouseEnter={e=>e.currentTarget.style.background="var(--fc-bg-light)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>

              {/* Avatar */}
              <div style={{ width:32, height:32, borderRadius:99, background:color+"18", border:`1.5px solid ${color}30`, flexShrink:0, overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center" }}>
                {imgSrc
                  ? <img src={imgSrc} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} onError={e=>e.target.style.display="none"} />
                  : <i className="fa-solid fa-user" style={{fontSize:14,color:"var(--fc-text-subtle)"}} />
                }
              </div>

              {/* Name + role + location */}
              <div style={{ flex:1, minWidth:0 }}>
                <Link to={`/resources/details/${item.user_slug}`}
                  style={{ fontSize:12, fontWeight:700, color:"var(--fc-text-primary)", textDecoration:"none", display:"block", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginBottom:2 }}>
                  {item.resource_name}
                </Link>
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  {item.user_profile_role && (
                    <span style={{ fontSize:11, fontWeight:600, color:"#2563EB", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", lineHeight:1.3 }}>
                      {item.user_profile_role}
                    </span>
                  )}
                  {item.location && (
                    <span style={{ fontSize:11, color:"var(--fc-text-subtle)", display:"flex", alignItems:"center", gap:3, whiteSpace:"nowrap" }}>
                      <i className="fa-solid fa-location-dot" style={{fontSize:8,color:"var(--fc-text-hint)"}}/>{item.location}
                    </span>
                  )}
                </div>
              </div>

              {/* Status */}
              {isOwnFirm ? (
                <button onClick={() => openStatus(item)}
                  style={{ padding:"3px 9px", borderRadius:99, border:`1.5px solid ${ss.color}30`, background:ss.bg, color:ss.color, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:5, whiteSpace:"nowrap", flexShrink:0 }}>
                  <div style={{ width:6, height:6, borderRadius:"50%", background:ss.color }} />
                  {ss.label}
                  <i className="fa-solid fa-chevron-down" style={{fontSize:8,opacity:0.6}} />
                </button>
              ) : (
                <span style={{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:99, background:ss.bg, color:ss.color, display:"flex", alignItems:"center", gap:5, whiteSpace:"nowrap", flexShrink:0 }}>
                  <div style={{ width:6, height:6, borderRadius:"50%", background:ss.color }} />
                  {ss.label}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Status update modal */}
      {statusModal && (
        <Modal title="Change Status" maxWidth={400}
          footer={
            <ModalFooter
              onCancel={()=>setStatusModal(null)}
              onConfirm={updateStatus}
              confirmDisabled={updating||!selStatus}
              confirmBusy={updating}
              confirmLabel={updating ? "Updating..." : "Update Status"}
            />
          }>
          <div style={{ fontSize:12, color:"var(--fc-text-subtle)", marginTop:-10, marginBottom:16 }}>{statusModal.resource_name}</div>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-secondary)", display:"block", marginBottom:6, textTransform:"uppercase" }}>Select a status</label>
            <select value={selStatus} onChange={e=>setSelStatus(e.target.value)} className="form-select" style={{fontSize:13, background:"var(--fc-card-bg)", color:"var(--fc-text-primary)", borderColor:"var(--fc-border)"}}>
              <option value="">Please select a status</option>
              {statusList.map(s => (
                <option key={s.status_id} value={s.status_id}>{s.SL} - {s.title}</option>
              ))}
            </select>
          </div>
          {selStatus === "3" && (
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-secondary)", display:"block", marginBottom:6, textTransform:"uppercase" }}>Note (optional)</label>
              <textarea value={note} onChange={e=>setNote(e.target.value)} rows={3}
                style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", border:"1.5px solid var(--fc-border)", borderRadius:9, fontSize:13, outline:"none", fontFamily:"inherit", resize:"vertical", background:"var(--fc-card-bg)", color:"var(--fc-text-primary)" }} />
            </div>
          )}
        </Modal>
      )}
    </>
  );
}

// ── AI Matching ───────────────────────────────────────────────────────────────